// @owners { team: patients-team }
import { useMemo } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartEssentialsPrice } from '~shared/features/essentials/hooks';
import { useScheduleableNonEssentialPrescriptions } from '~shared/features/my-meds/hooks/useScheduleablePrescriptions';
import { useNextAvailableDateAddressId } from '~shared/features/next-available-date/hooks/useNextAvailableDateAddressId';
import { getAutobillPrescriptionLoadingByID } from '~shared/features/pricing/selectors/getPricing';
import { useOrderPricing } from '~shared/queries';
import { useSelectorShared } from '~shared/store';
import { useQueryEssentialsFeeConfigs } from '../queries/useQueryEssentialsFeeConfigs';

export const useEssentialsCartThreshold = () => {
  const { essentialsMinimumBasket } = useQueryEssentialsFeeConfigs();
  const { isOrderPricingLoading } = useOrderPricing();
  const { prescriptionIDs: cartPrescriptionIDs } = usePrescriptionsInCart();
  const autobillLoadingByID = useSelectorShared(getAutobillPrescriptionLoadingByID);
  const nextAvailableDateAddressID = useNextAvailableDateAddressId();
  const scheduleableNonEssential = useScheduleableNonEssentialPrescriptions({ nextAvailableDateAddressID });
  const hasScheduleablePrescriptions = scheduleableNonEssential.length > 0;
  const { essentialsOnlyPriceMinRemainder, essentialsOnlyPriceBelowMinimum } = useCartEssentialsPrice();
  const updatingPricing = isOrderPricingLoading || cartPrescriptionIDs.some((rxID) => autobillLoadingByID[rxID]);

  return useMemo(
    () => ({
      showEssentialsThreshold: essentialsOnlyPriceBelowMinimum,
      orderMinimumRemainder: essentialsOnlyPriceMinRemainder,
      minimumThreshold: essentialsMinimumBasket?.value,
      minimumFooterCopy: hasScheduleablePrescriptions ? 'Add a prescription or spend' : 'Spend',
      minimumAlertCopy: hasScheduleablePrescriptions
        ? 'Add more Essentials or a prescription to checkout'
        : 'Add more Essentials to checkout',
      updatingPricing,
    }),
    [
      essentialsMinimumBasket?.value,
      essentialsOnlyPriceBelowMinimum,
      essentialsOnlyPriceMinRemainder,
      hasScheduleablePrescriptions,
      updatingPricing,
    ],
  );
};
